import { ResourceType } from "@common/types/auditLog";
import {
    ActivityProcessName,
    APIProcessName,
    RPCProcessName,
    SQSProcessName,
} from "../process-names";
import { BANK_RECONCILIATION_SOURCE_TYPES } from "./bank-reconciliation-source-types";
import type { SourceTypeTemplate } from "./history-source-config";
import {
    applyTransactionUpdate,
    checkAndMergeSimilarCounterparties,
    normalizeCategorizationForTheSameTransactions,
} from "./enrichment-activity-source-types";
import { TRANSACTION_SOURCE_TYPE_TEMPLATES } from "./transaction-sources";
import { ORGANIZATION_MEMBER_SOURCE_TYPE_TEMPLATES } from "./organization-history-sources";

const RECONCILED_TRANSACTION_TEMPLATE: SourceTypeTemplate = {
    verb: "Reconciled",
    noun: "transaction",
};

export const IN_APP_HISTORY_SOURCE_TYPE_TEMPLATES: Record<
    string,
    SourceTypeTemplate
> = {
    [APIProcessName.REPORT_SETTINGS_UPDATE]: {
        verb: "Updated",
        noun: "report settings",
    },
    [APIProcessName.REPORT_SETTINGS_TEMPLATE_APPLY]: {
        verb: "Applied",
        noun: "report template",
    },
    [APIProcessName.REPORT_SETTINGS_TEMPLATE_REMOVE]: {
        verb: "Removed",
        noun: "report template",
    },
    [APIProcessName.SCHEDULE_TEMPLATE_CREATE]: {
        verb: "Created",
        noun: "policy",
    },
    [APIProcessName.SCHEDULE_TEMPLATE_UPDATE]: {
        verb: "Updated",
        noun: "policy",
    },
    [APIProcessName.SCHEDULE_TEMPLATE_ARCHIVE]: {
        verb: "Archived",
        noun: "policy",
    },
    [APIProcessName.SCHEDULE_TEMPLATE_APPLY]: {
        verb: "Applied",
        noun: "schedule",
    },
    [APIProcessName.SCHEDULE_TEMPLATE_APPLY_BULK]: {
        verb: "Applied",
        noun: "schedule",
    },
    [SQSProcessName.SCHEDULE_RULE_APPLY]: {
        verb: "Applied",
        noun: "schedule",
    },
    [SQSProcessName.SCHEDULE_RULE_SUGGEST]: {
        verb: "Suggested",
        noun: "schedule",
    },
    [APIProcessName.SCHEDULE_REVIEW_CONFIRM_DRAFTS]: {
        verb: "Activated",
        noun: "schedule",
    },
    [APIProcessName.SCHEDULE_REVIEW_DISMISS_DRAFTS]: {
        verb: "Dismissed",
        noun: "schedule",
    },
    [APIProcessName.SCHEDULE_APPLICATION_CHANGE]: {
        verb: "Changed",
        noun: "schedule",
    },
    [APIProcessName.SCHEDULE_APPLICATION_PAUSE]: {
        verb: "Paused recognition on",
        noun: "schedule",
    },
    [APIProcessName.SCHEDULE_APPLICATION_RESUME]: {
        verb: "Resumed recognition on",
        noun: "schedule",
    },
    [APIProcessName.SCHEDULE_APPLICATION_CANCEL]: {
        verb: "Stopped",
        noun: "schedule",
    },
    [APIProcessName.SCHEDULE_APPLICATION_DELETE]: {
        verb: "Removed",
        noun: "schedule",
    },
    [APIProcessName.SCHEDULE_REVIEW_CONFIRM_RECOGNITION]: {
        verb: "Posted recognition on",
        noun: "schedule",
    },
    [APIProcessName.SCHEDULE_REVIEW_CONFIRM_ALL_RECOGNITION]: {
        verb: "Posted recognition on",
        noun: "schedule",
    },
    [SQSProcessName.SCHEDULE_RECOGNITION_AUTO_POST]: {
        verb: "Posted recognition on",
        noun: "schedule",
    },
    [APIProcessName.CHART_OF_ACCOUNTS_TEMPLATE_APPLY]: {
        verb: "Applied",
        noun: "chart of accounts template",
    },
    [APIProcessName.CHART_OF_ACCOUNTS_TEMPLATE_APPLY_UNDO]: {
        verb: "Reverted",
        noun: "chart of accounts template",
    },
    [APIProcessName.ENTITY_CREATE]: {
        resourceTypeOverrides: {
            [ResourceType.chartOfAccountsTemplateApplyEvent]: {
                verb: "Set up",
                noun: "chart of accounts from template",
                suffix: "when adding entity",
            },
        },
    },
    [APIProcessName.ORGANIZATION_CREATE_CLIENT]: {
        resourceTypeOverrides: {
            [ResourceType.chartOfAccountsTemplateApplyEvent]: {
                verb: "Set up",
                noun: "chart of accounts from template",
                suffix: "when adding client",
            },
        },
    },
    ...TRANSACTION_SOURCE_TYPE_TEMPLATES,
    ...Object.fromEntries(
        BANK_RECONCILIATION_SOURCE_TYPES.map((sourceType) => [
            sourceType,
            RECONCILED_TRANSACTION_TEMPLATE,
        ]),
    ),
    [APIProcessName.RECONCILE_LOAN]: {
        verb: "Reconciled",
        noun: "loan",
    },
    [APIProcessName.BANK_RECONCILIATION_DELETE]: {
        verb: "Deleted",
        noun: "reconciliation",
    },
    [APIProcessName.TASK_CREATE]: {
        verb: "Created",
        noun: "task",
    },
    [APIProcessName.TASK_UPDATE]: {
        verb: "Updated",
        noun: "task",
    },
    [APIProcessName.TASK_DELETE]: {
        verb: "Deleted",
        noun: "task",
    },
    [APIProcessName.CLOSE_TEMPLATE_APPLY]: {
        verb: "Created",
        noun: "task",
        resourceTypeOverrides: {
            [ResourceType.CLOSE]: {
                verb: "Created",
                noun: "work item",
            },
        },
    },
    [APIProcessName.DELETE_CLOSE]: {
        verb: "Deleted",
        noun: "task",
    },
    [APIProcessName.TASK_BULK_UPDATE]: {
        verb: "Updated",
        noun: "task",
    },
    [SQSProcessName.TASK_BULK_UPDATES]: {
        verb: "Updated",
        noun: "task",
    },
    [APIProcessName.CANCEL_CLOSE]: {
        verb: "Canceled",
        noun: "task",
        skipFieldLabel: true,
    },
    [APIProcessName.UPDATE_CLOSE]: {
        verb: "Updated",
        noun: "task",
        resourceTypeOverrides: {
            [ResourceType.CLOSE]: { verb: "Updated", noun: "work item" },
        },
    },
    [SQSProcessName.CREATE_ACCRUAL_REVIEW_TASKS_FOR_ENTITY]: {
        verb: "Created",
        noun: "task",
    },
    [SQSProcessName.CREATE_SCHEDULE_REVIEW_TASKS_FOR_ENTITY]: {
        verb: "Created",
        noun: "task",
    },
    [APIProcessName.COUNTERPARTY_CREATE]: {
        verb: "Created",
        noun: "counterparty",
        resourceTypeOverrides: {
            [ResourceType.INVOICE]: {
                verb: "Updated counterparty for",
                noun: "invoice",
                skipFieldLabel: true,
            },
            [ResourceType.BILL]: {
                verb: "Updated counterparty for",
                noun: "bill",
                skipFieldLabel: true,
            },
            [ResourceType.TRANSACTION]: {
                verb: "Updated counterparty for",
                noun: "transaction",
                skipFieldLabel: true,
            },
        },
    },
    [APIProcessName.COUNTERPARTY_UPDATE]: {
        verb: "Updated",
        noun: "counterparty",
    },
    [APIProcessName.COUNTERPARTY_TIN_REVEAL]: {
        verb: "Revealed tax ID",
        noun: "counterparty",
        skipFieldLabel: true,
    },
    [APIProcessName.COUNTERPARTY_TIN_EXPORT]: {
        verb: "Exported tax ID",
        pluralVerb: "Exported tax IDs",
        noun: "counterparty",
        skipFieldLabel: true,
    },
    [APIProcessName.COPY_GLOBAL_COUNTERPARTY]: {
        verb: "Created",
        noun: "counterparty",
        resourceTypeOverrides: {
            [ResourceType.INVOICE]: {
                verb: "Updated counterparty for",
                noun: "invoice",
                skipFieldLabel: true,
            },
            [ResourceType.BILL]: {
                verb: "Updated counterparty for",
                noun: "bill",
                skipFieldLabel: true,
            },
            [ResourceType.TRANSACTION]: {
                verb: "Updated counterparty for",
                noun: "transaction",
                skipFieldLabel: true,
            },
        },
    },
    [APIProcessName.DELETE_COUNTERPARTY]: {
        verb: "Deleted",
        noun: "counterparty",
    },
    [APIProcessName.COUNTERPARTY_CONTACT_CREATE]: {
        verb: "Updated",
        noun: "counterparty",
    },
    [APIProcessName.COUNTERPARTY_CONTACT_UPDATE]: {
        verb: "Updated",
        noun: "counterparty",
    },
    [APIProcessName.COUNTERPARTY_CONTACT_DELETE]: {
        verb: "Updated",
        noun: "counterparty",
    },
    [APIProcessName.COUNTERPARTY_CONTACT_SET_PRIMARY]: {
        verb: "Updated",
        noun: "counterparty",
    },
    [APIProcessName.COUNTERPARTY_ADDRESS_CREATE]: {
        verb: "Updated",
        noun: "counterparty",
    },
    [APIProcessName.COUNTERPARTY_ADDRESS_UPDATE]: {
        verb: "Updated",
        noun: "counterparty",
    },
    [APIProcessName.COUNTERPARTY_ADDRESS_DELETE]: {
        verb: "Updated",
        noun: "counterparty",
    },
    [APIProcessName.COUNTERPARTY_ADDRESS_SET_PRIMARY]: {
        verb: "Updated",
        noun: "counterparty",
    },
    [APIProcessName.ADMIN_EDIT_COUNTERPARTY]: {
        verb: "Updated",
        noun: "counterparty",
    },
    [APIProcessName.CLASS_CREATE]: {
        verb: "Created",
        noun: "class",
    },
    [APIProcessName.CLASS_UPDATE]: {
        verb: "Updated",
        noun: "class",
    },
    [APIProcessName.CLASS_DELETE]: {
        verb: "Deleted",
        noun: "class",
    },
    [APIProcessName.CLASS_CHANGE_POSITION]: {
        verb: "Repositioned",
        noun: "class",
    },
    [APIProcessName.CATALOG_ITEM_CREATE]: {
        verb: "Created",
        noun: "catalog item",
    },
    [APIProcessName.CATALOG_ITEM_UPDATE]: {
        verb: "Updated",
        noun: "catalog item",
    },
    [APIProcessName.CATALOG_ITEM_DELETE]: {
        verb: "Deleted",
        noun: "catalog item",
    },
    [APIProcessName.CATALOG_ITEM_DUPLICATE]: {
        verb: "Duplicated",
        noun: "catalog item",
    },
    [APIProcessName.CATALOG_ITEM_BULK_SET_ACTIVE]: {
        verb: "Updated",
        noun: "catalog item",
    },
    [APIProcessName.CATALOG_ITEM_ARCHIVE]: {
        verb: "Archived",
        noun: "catalog item",
        skipFieldLabel: true,
    },
    [APIProcessName.CATALOG_ITEM_ENABLE]: {
        verb: "Enabled",
        noun: "catalog item",
        skipFieldLabel: true,
    },
    [APIProcessName.INVOICE_TEMPLATE_CREATE]: {
        verb: "Created",
        noun: "invoice template",
    },
    [APIProcessName.INVOICE_TEMPLATE_CREATE_FROM_INVOICE]: {
        verb: "Created",
        noun: "invoice template",
        suffix: "from an invoice",
    },
    [APIProcessName.INVOICE_TEMPLATE_RENAME]: {
        verb: "Renamed",
        noun: "invoice template",
        skipFieldLabel: true,
    },
    [APIProcessName.INVOICE_TEMPLATE_ARCHIVE]: {
        verb: "Archived",
        noun: "invoice template",
        skipFieldLabel: true,
    },
    [APIProcessName.INVOICE_TEMPLATE_UNARCHIVE]: {
        verb: "Unarchived",
        noun: "invoice template",
        skipFieldLabel: true,
    },
    [APIProcessName.INVOICE_TEMPLATE_DELETE]: {
        verb: "Deleted",
        noun: "invoice template",
    },
    [APIProcessName.INVOICE_TEMPLATE_UPDATE_CONTENT]: {
        verb: "Updated",
        noun: "invoice template content",
    },
    [APIProcessName.COA_ACCOUNT_CREATE]: {
        verb: "Created",
        noun: "account",
    },
    [APIProcessName.COA_CHART_EDITOR_ACCOUNT_ADD]: {
        verb: "Created",
        noun: "account",
    },
    [APIProcessName.COA_ACCOUNT_UPDATE]: {
        verb: "Updated",
        noun: "account",
    },
    [APIProcessName.COA_CHART_EDITOR_ACCOUNT_UPDATE]: {
        verb: "Updated",
        noun: "account",
    },
    [APIProcessName.COA_ACCOUNT_DISABLE]: {
        verb: "Archived",
        noun: "account",
    },
    [APIProcessName.COA_CHART_EDITOR_ACCOUNT_DISABLED]: {
        verb: "Archived",
        noun: "account",
    },
    [APIProcessName.COA_ACCOUNT_ENABLE]: {
        verb: "Unarchived",
        noun: "account",
    },
    [APIProcessName.COA_CHART_EDITOR_ACCOUNT_ENABLED]: {
        verb: "Unarchived",
        noun: "account",
    },
    [APIProcessName.COA_ACCOUNT_DELETE]: {
        verb: "Deleted",
        noun: "account",
    },
    [APIProcessName.COA_ACCOUNT_MERGE]: {
        verb: "Merged",
        noun: "account",
    },
    [APIProcessName.COA_ACCOUNT_ROLE_REASSIGN]: {
        verb: "Reassigned role for",
        noun: "account",
    },
    [APIProcessName.ACCOUNTING_REMOVE_OPENING_BALANCE]: {
        verb: "Removed",
        noun: "account",
    },
    [APIProcessName.COA_CHART_EDITOR_DELETE]: {
        verb: "Deleted",
        noun: "account",
        resourceTypeOverrides: {
            [ResourceType.ACCOUNT_GROUP]: {
                verb: "Deleted",
                noun: "account group",
            },
        },
    },
    [APIProcessName.COA_CHART_EDITOR_MOVE]: {
        verb: "Updated",
        noun: "account",
        resourceTypeOverrides: {
            [ResourceType.ACCOUNT_GROUP]: {
                verb: "Updated",
                noun: "account group",
            },
        },
    },
    [APIProcessName.COA_ENTITY_ACCOUNT_GROUP_CREATE]: {
        verb: "Created",
        noun: "account group",
        resourceTypeOverrides: {
            [ResourceType.ACCOUNT]: {
                verb: "Updated",
                noun: "account",
            },
        },
    },
    [APIProcessName.COA_ENTITY_ACCOUNT_GROUP_UPDATE]: {
        verb: "Updated",
        noun: "account group",
        resourceTypeOverrides: {
            [ResourceType.ACCOUNT]: {
                verb: "Updated",
                noun: "account",
            },
        },
    },
    [APIProcessName.COA_ENTITY_ACCOUNT_GROUP_DELETE]: {
        verb: "Deleted",
        noun: "account group",
        resourceTypeOverrides: {
            [ResourceType.ACCOUNT]: {
                verb: "Updated",
                noun: "account",
            },
        },
    },
    [APIProcessName.COA_CHART_EDITOR_GROUP_ADD]: {
        verb: "Created",
        noun: "account group",
    },
    [APIProcessName.COA_CHART_EDITOR_GROUP_RENAME]: {
        verb: "Updated",
        noun: "account group",
    },
    [APIProcessName.FINANCIAL_ACCOUNT_UPDATE]: {
        verb: "Updated",
        noun: "account",
    },
    [APIProcessName.FINANCIAL_ACCOUNT_DELETE]: {
        verb: "Deleted",
        noun: "account",
    },
    [APIProcessName.FINANCIAL_ACCOUNT_MERGE_ACCOUNTS]: {
        verb: "Merged",
        noun: "account",
        resourceTypeOverrides: {
            [ResourceType.TRANSACTION]: {
                verb: "Updated",
                noun: "transaction",
                suffix: "through account merge",
            },
        },
    },
    [SQSProcessName.FINANCIAL_ACCOUNT_MERGING]: {
        verb: "Merged",
        noun: "account",
        resourceTypeOverrides: {
            [ResourceType.TRANSACTION]: {
                verb: "Updated",
                noun: "transaction",
                suffix: "through account merge",
            },
        },
    },
    [SQSProcessName.ACCOUNT_ENTITY_CHANGE]: {
        verb: "Updated",
        noun: "account",
        resourceTypeOverrides: {
            [ResourceType.TRANSACTION]: {
                verb: "Updated",
                noun: "transaction",
                suffix: "through account entity change",
            },
        },
    },
    [SQSProcessName.TRANSACTION_RULE_APPLICATION]: {
        verb: "Updated",
        noun: "transaction",
    },
    [APIProcessName.TRANSACTION_FIELD_REVIEW_ADD]: {
        verb: "Updated review for",
        noun: "transaction",
        skipFieldLabel: true,
    },
    [APIProcessName.TRANSACTION_FIELD_REVIEW_REVOKE]: {
        verb: "Updated review for",
        noun: "transaction",
        skipFieldLabel: true,
    },
    [APIProcessName.TRANSACTION_FIELD_REVIEW_BULK_ADD]: {
        verb: "Updated review for",
        noun: "transaction",
        suffix: "through a bulk update",
        skipFieldLabel: true,
    },
    [APIProcessName.TRANSACTION_FIELD_REVIEW_BULK_REVOKE]: {
        verb: "Updated review for",
        noun: "transaction",
        suffix: "through a bulk update",
        skipFieldLabel: true,
    },
    [APIProcessName.COUNTERPARTY_FIELD_REVIEW_ADD]: {
        verb: "Updated review for",
        noun: "counterparty",
        skipFieldLabel: true,
    },
    [APIProcessName.COUNTERPARTY_FIELD_REVIEW_REVOKE]: {
        verb: "Updated review for",
        noun: "counterparty",
        skipFieldLabel: true,
    },
    [APIProcessName.ACCRUAL_REVIEW_APPROVE]: {
        verb: "Updated",
        noun: "transaction",
        resourceTypeOverrides: {
            [ResourceType.INVOICE]: {
                verb: "Updated",
                noun: "invoice",
            },
            [ResourceType.BILL]: {
                verb: "Updated",
                noun: "bill",
            },
        },
    },
    [APIProcessName.ACCRUAL_REVIEW_BULK_APPROVE]: {
        verb: "Updated",
        noun: "transaction",
        suffix: "through a bulk update",
        resourceTypeOverrides: {
            [ResourceType.INVOICE]: {
                verb: "Updated",
                noun: "invoice",
                suffix: "through a bulk update",
            },
            [ResourceType.BILL]: {
                verb: "Updated",
                noun: "bill",
                suffix: "through a bulk update",
            },
        },
    },
    [APIProcessName.ACCRUAL_REVIEW_UNDO]: {
        verb: "Updated",
        noun: "transaction",
    },
    [APIProcessName.ACCRUAL_REVIEW_BULK_UNMATCH_INVOICES]: {
        verb: "Updated",
        noun: "transaction",
        suffix: "through a bulk update",
    },
    [APIProcessName.ACCRUAL_MATCH_CREATE]: {
        verb: "Accrual matched",
        noun: "transaction",
        skipFieldLabel: true,
    },
    [APIProcessName.ACCRUAL_MATCH_REMOVE]: {
        verb: "Accrual unmatched",
        noun: "transaction",
        skipFieldLabel: true,
    },
    [SQSProcessName.INVOICE_NATIVE_EMAIL_SEND]: {
        verb: "Sent",
        noun: "invoice email",
        skipFieldLabel: true,
    },
    [ActivityProcessName.TRANSACTION_ENRICHMENT]: {
        verb: "Updated",
        noun: "transaction",
    },
    [ActivityProcessName.TRANSACTION_ENRICHMENT_NORMALIZATION]: {
        verb: "Updated",
        noun: "transaction",
    },
    [ActivityProcessName.TRANSACTION_COUNTERPARTY_ENRICHMENT]: {
        verb: "Updated",
        noun: "transaction",
    },
    [ActivityProcessName.TRANSACTION_ENRICHMENT_SIDE_EFFECTS]: {
        verb: "Updated",
        noun: "transaction",
    },
    [ActivityProcessName.TRANSACTION_SPLIT_RULE_APPLICATION]: {
        verb: "Updated",
        noun: "transaction",
    },
    [SQSProcessName.AUDIT_LOG_ROLLBACK]: {
        verb: "Reverted",
        noun: "transaction",
    },
    [SQSProcessName.MERCURY_ACCOUNTS_SYNC]: {
        verb: "Created",
        noun: "transaction",
        resourceTypeOverrides: {
            [ResourceType.FINANCIAL_DOCUMENT]: {
                verb: "Created",
                noun: "statement",
            },
        },
    },
    [SQSProcessName.GUSTO_ACCOUNT_SYNC]: {
        verb: "Created",
        noun: "transaction",
    },
    [SQSProcessName.ADP_RUN_ACCOUNT_SYNC]: {
        verb: "Created",
        noun: "transaction",
    },
    [SQSProcessName.RAMP_TRANSACTIONS_SYNC]: {
        verb: "Created",
        noun: "transaction",
    },
    [SQSProcessName.PAYPAL_SYNC]: {
        verb: "Created",
        noun: "transaction",
    },
    [SQSProcessName.BILLCOM_SYNC]: {
        verb: "Created",
        noun: "transaction",
    },
    [SQSProcessName.PLAID_INITIAL_TRANSACTION_IMPORT]: {
        verb: "Created",
        noun: "transaction",
    },
    [SQSProcessName.PLAID_TRANSACTION_IMPORT]: {
        verb: "Created",
        noun: "transaction",
    },
    [SQSProcessName.STRIPE_TRANSACTIONS_SYNC]: {
        verb: "Created",
        noun: "transaction",
    },
    [SQSProcessName.STRIPE_INVOICES_SYNC]: {
        verb: "Created",
        noun: "invoice",
    },
    [SQSProcessName.STRIPE_SYNC]: {
        verb: "Created",
        noun: "transaction",
        resourceTypeOverrides: {
            [ResourceType.INVOICE]: {
                verb: "Created",
                noun: "invoice",
            },
        },
    },
    [APIProcessName.PLAID_WEBHOOK_SYNC_UPDATES_AVAILABLE]: {
        verb: "Created",
        noun: "transaction",
    },
    [APIProcessName.PLAID_WEBHOOK_DEFAULT_UPDATE]: {
        verb: "Created",
        noun: "transaction",
    },
    [APIProcessName.PLAID_WEBHOOK_HISTORICAL_UPDATE]: {
        verb: "Created",
        noun: "transaction",
    },
    [APIProcessName.PLAID_WEBHOOK_TRANSACTIONS_REMOVED]: {
        verb: "Deleted",
        noun: "transaction",
    },
    [applyTransactionUpdate]: {
        verb: "Updated",
        noun: "transaction",
    },
    [normalizeCategorizationForTheSameTransactions]: {
        verb: "Updated",
        noun: "transaction",
    },
    [checkAndMergeSimilarCounterparties]: {
        verb: "Updated",
        noun: "transaction",
    },
    [APIProcessName.RULE_CREATE]: {
        verb: "Created",
        noun: "rule",
        resourceTypeOverrides: {
            [ResourceType.ACCOUNTING_RULE]: {
                verb: "Created",
                noun: "accounting rule",
            },
            [ResourceType.TRANSACTION_RULE]: {
                verb: "Created",
                noun: "transaction rule",
            },
            [ResourceType.TRANSFER_RULE]: {
                verb: "Created",
                noun: "transfer rule",
            },
            [ResourceType.SPLIT_RULE]: {
                verb: "Created",
                noun: "split rule",
            },
        },
    },
    [APIProcessName.RULE_UPDATE]: {
        verb: "Updated",
        noun: "rule",
        resourceTypeOverrides: {
            [ResourceType.ACCOUNTING_RULE]: {
                verb: "Updated",
                noun: "accounting rule",
            },
            [ResourceType.TRANSACTION_RULE]: {
                verb: "Updated",
                noun: "transaction rule",
            },
            [ResourceType.TRANSFER_RULE]: {
                verb: "Updated",
                noun: "transfer rule",
            },
            [ResourceType.SPLIT_RULE]: {
                verb: "Updated",
                noun: "split rule",
            },
        },
    },
    [APIProcessName.RULE_DELETE]: {
        verb: "Deleted",
        noun: "rule",
        resourceTypeOverrides: {
            [ResourceType.ACCOUNTING_RULE]: {
                verb: "Deleted",
                noun: "accounting rule",
            },
            [ResourceType.TRANSACTION_RULE]: {
                verb: "Deleted",
                noun: "transaction rule",
            },
            [ResourceType.TRANSFER_RULE]: {
                verb: "Deleted",
                noun: "transfer rule",
            },
            [ResourceType.SPLIT_RULE]: {
                verb: "Deleted",
                noun: "split rule",
            },
        },
    },
    [APIProcessName.RULE_NOTE_UPDATE]: {
        verb: "Updated",
        noun: "rule note",
    },
    [APIProcessName.RULE_GROUP_CREATE]: {
        verb: "Created",
        noun: "rule group",
    },
    [APIProcessName.RULE_GROUP_UPDATE]: {
        verb: "Updated",
        noun: "rule group",
    },
    [APIProcessName.RULE_GROUP_DELETE]: {
        verb: "Deleted",
        noun: "rule group",
    },
    [APIProcessName.ACCOUNTING_JOURNAL_ENTRIES_CREATE]: {
        verb: "Created",
        noun: "journal entry",
    },
    [APIProcessName.ACCOUNTING_JOURNAL_ENTRIES_BULK_CREATE]: {
        verb: "Created",
        noun: "journal entry",
    },
    [APIProcessName.ACCOUNTING_JOURNAL_ENTRIES_UPDATE]: {
        verb: "Updated",
        noun: "journal entry",
    },
    [APIProcessName.ACCOUNTING_JOURNAL_ENTRIES_DELETE]: {
        verb: "Deleted",
        noun: "journal entry",
    },
    [APIProcessName.TRANSACTION_CREATE_LINK]: {
        verb: "Linked",
        noun: "receipt",
        skipFieldLabel: true,
    },
    [APIProcessName.FINANCIAL_DOCUMENT_CREATE_LINK]: {
        verb: "Linked",
        noun: "receipt",
        skipFieldLabel: true,
    },
    [APIProcessName.TRANSACTION_UPLOAD_RECEIPT]: {
        verb: "Linked",
        noun: "receipt",
        skipFieldLabel: true,
    },
    [APIProcessName.TRANSACTION_DETACH_FINANCIAL_DOCUMENT]: {
        verb: "Unlinked",
        noun: "receipt",
        skipFieldLabel: true,
    },
    [APIProcessName.FINANCIAL_DOCUMENT_DETACH_FINANCIAL_TRANSACTION]: {
        verb: "Unlinked",
        noun: "receipt",
        skipFieldLabel: true,
    },
    [APIProcessName.BILL_CREATE_MANUAL]: {
        verb: "Created",
        noun: "bill",
    },
    [APIProcessName.BILL_REPLACE_IMPORTER]: {
        verb: "Replaced",
        noun: "bill",
    },
    [APIProcessName.BILL_PATCH_IMPORTER]: {
        verb: "Updated",
        noun: "bill",
    },
    [APIProcessName.BILL_UPDATE]: {
        verb: "Updated",
        noun: "bill",
    },
    [APIProcessName.BILL_DELETE]: {
        verb: "Deleted",
        noun: "bill",
    },
    [APIProcessName.BILL_LINE_ITEM_UPDATE]: {
        verb: "Updated",
        noun: "bill",
    },
    [APIProcessName.BILL_LINE_ITEM_BULK_UPDATE]: {
        verb: "Updated",
        noun: "bill",
        suffix: "through a bulk update",
    },
    [APIProcessName.BILL_DOCUMENT_LINK]: {
        verb: "Linked document to",
        noun: "bill",
        skipFieldLabel: true,
    },
    [APIProcessName.BILL_DOCUMENT_UNLINK]: {
        verb: "Unlinked document from",
        noun: "bill",
        skipFieldLabel: true,
    },
    [SQSProcessName.RAMP_BILLS_SYNC]: {
        verb: "Created",
        noun: "bill",
    },
    [APIProcessName.INVOICE_CREATE_MANUAL]: {
        verb: "Created",
        noun: "invoice",
    },
    [APIProcessName.INVOICE_REPLACE_IMPORTER]: {
        verb: "Replaced",
        noun: "invoice",
    },
    [APIProcessName.INVOICE_PATCH_IMPORTER]: {
        verb: "Updated",
        noun: "invoice",
    },
    [APIProcessName.INVOICE_UPDATE]: {
        verb: "Updated",
        noun: "invoice",
    },
    [APIProcessName.INVOICE_DELETE]: {
        verb: "Deleted",
        noun: "invoice",
    },
    [APIProcessName.INVOICE_LINE_ITEM_UPDATE]: {
        verb: "Updated",
        noun: "invoice",
    },
    [APIProcessName.INVOICE_LINE_ITEM_BULK_UPDATE]: {
        verb: "Updated",
        noun: "invoice",
        suffix: "through a bulk update",
    },
    [APIProcessName.INVOICE_DOCUMENT_LINK]: {
        verb: "Linked document to",
        noun: "invoice",
        skipFieldLabel: true,
    },
    [APIProcessName.INVOICE_DOCUMENT_UNLINK]: {
        verb: "Unlinked document from",
        noun: "invoice",
        skipFieldLabel: true,
    },
    [APIProcessName.FINANCIAL_DOCUMENT_UPLOAD_DOCUMENT]: {
        verb: "Created",
        noun: "document",
    },
    [APIProcessName.FINANCIAL_DOCUMENT_DELETE_DOCUMENT]: {
        verb: "Deleted",
        noun: "document",
        resourceTypeOverrides: {
            [ResourceType.MANUAL_JOURNAL_ENTRY]: {
                verb: "Updated",
                noun: "journal entry",
            },
            [ResourceType.JOURNAL_ENTRY]: {
                verb: "Updated",
                noun: "journal entry",
            },
            [ResourceType.TRANSACTION]: {
                verb: "Updated",
                noun: "transaction",
            },
        },
    },
    [APIProcessName.FINANCIAL_DOCUMENT_PROCESS_ARCHIVE]: {
        verb: "Created",
        noun: "document",
        suffix: "from archive",
    },
    [APIProcessName.FINANCIAL_DOCUMENT_BULK_ACTION]: {
        verb: "Updated",
        noun: "document",
        suffix: "through a bulk update",
    },
    [APIProcessName.EXPORT_SAVE_DOCUMENT]: {
        verb: "Created",
        noun: "document",
        suffix: "from export",
    },
    [APIProcessName.FINANCIAL_DOCUMENT_UPDATE_DOCUMENT_CATEGORY]: {
        verb: "Recategorized",
        noun: "document",
    },
    [APIProcessName.FINANCIAL_DOCUMENT_CHANGE_ENTITY]: {
        verb: "Reassigned",
        noun: "document",
    },
    [APIProcessName.FINANCIAL_DOCUMENT_SCAN_DOCUMENT]: {
        verb: "Scanned",
        noun: "document",
    },
    [APIProcessName.FINANCIAL_DOCUMENT_RUN_LOAN_EXTRACTION]: {
        verb: "Updated",
        noun: "document",
        suffix: "with loan extraction",
    },
    [APIProcessName.FINANCIAL_DOCUMENT_ADD_TO_IMPORTER]: {
        verb: "Added to importer",
        noun: "document",
    },
    [APIProcessName.FINANCIAL_DOCUMENT_EXTRACT_ACCOUNT_INFO]: {
        verb: "Updated",
        noun: "document",
        suffix: "with extracted account info",
    },
    [APIProcessName.FINANCIAL_DOCUMENT_UPDATE_TAXONOMY_EXTRACTION]: {
        verb: "Updated",
        noun: "document",
        suffix: "with taxonomy extraction",
    },
    [APIProcessName.FINANCIAL_DOCUMENT_REMOVE_IMPORTER_ERROR]: {
        verb: "Removed importer error from",
        noun: "document",
    },
    [APIProcessName.FINANCIAL_DOCUMENT_FINALIZE_TRANSACTIONS_IMPORT]: {
        verb: "Finalized transactions import for",
        noun: "document",
    },
    [APIProcessName.FINANCIAL_DOCUMENT_FINALIZE_INVOICES_IMPORT]: {
        verb: "Finalized invoices import for",
        noun: "document",
    },
    [APIProcessName.FINANCIAL_DOCUMENT_FINALIZE_BILLS_IMPORT]: {
        verb: "Finalized bills import for",
        noun: "document",
    },
    [APIProcessName.FINANCIAL_DOCUMENT_CHECK_FOR_IMPORTER_ERRORS]: {
        verb: "Checked importer errors for",
        noun: "document",
    },
    [APIProcessName.FINANCIAL_DOCUMENT_REMOVE_FROM_IMPORTER]: {
        verb: "Removed from importer",
        noun: "document",
    },
    [APIProcessName.FINANCIAL_DOCUMENT_RUN_TAXONOMY_EXTRACTION]: {
        verb: "Ran taxonomy extraction on",
        noun: "document",
    },
    [APIProcessName.FINANCIAL_DOCUMENT_UPDATE_ACCOUNT_INFO]: {
        verb: "Updated account info for",
        noun: "document",
    },
    [APIProcessName.FINANCIAL_DOCUMENT_UPDATE_DOCUMENT_METADATA]: {
        verb: "Updated",
        noun: "document",
    },
    [SQSProcessName.FINANCIAL_DOCUMENT_TRANSACTIONS_IMPORT]: {
        verb: "Imported transactions from",
        noun: "document",
        resourceTypeOverrides: {
            [ResourceType.TRANSACTION]: {
                verb: "Imported",
                noun: "transaction",
            },
        },
    },
    [SQSProcessName.FINANCIAL_DOCUMENT_ACCRUAL_IMPORT]: {
        verb: "Imported accrual document from",
        noun: "document",
    },
    [SQSProcessName.EXTRACT_ACCOUNT_INFO_FROM_DOCUMENT]: {
        verb: "Extracted account info from",
        noun: "document",
    },
    [SQSProcessName.DOCUMENT_SCANNING]: {
        verb: "Scanned",
        noun: "document",
    },
    [SQSProcessName.DOCUMENT_DOWNLOAD]: {
        verb: "Downloaded",
        noun: "document",
    },
    [APIProcessName.FINANCIAL_DOCUMENT_DOWNLOAD_FILE]: {
        verb: "Downloaded",
        noun: "document",
    },
    [APIProcessName.FINANCIAL_DOCUMENT_MCP_DOWNLOAD]: {
        verb: "Downloaded",
        noun: "document",
    },
    [SQSProcessName.REVALIDATE_IMPORTER_DOCUMENTS]: {
        verb: "Revalidated",
        noun: "document",
    },
    [RPCProcessName.FINANCIAL_DOCUMENT_UPLOADED]: {
        verb: "Created",
        noun: "document",
    },
    [RPCProcessName.FINANCIAL_DOCUMENT_RETRY_MATCHING]: {
        verb: "Retried matching",
        noun: "document",
    },
    [RPCProcessName.MATCH_FINANCIAL_DOCUMENT]: {
        verb: "Matched",
        noun: "document",
    },
    ...ORGANIZATION_MEMBER_SOURCE_TYPE_TEMPLATES,
};
